/**
 * SynthesisProgress.tsx — truthful "what's happening" indicator for document
 * synthesis. Driven by the REAL pipeline stage (kb_documents.synthesis_stage):
 * a playful constant headline + a caption that changes with the stage + a
 * progress bar (fase N/6). `full` for the Esplora waiting area, `compact` for
 * the Documenti table row. Emoji only (no I.* icons) → keeps the icon grep green.
 */

export const TOTAL_STEPS = 6;

/** Backend SynthesisStage → user-facing step number + playful caption. */
export const STAGE_STEPS: Record<string, { step: number; emoji: string; label: string }> = {
  pending: { step: 1, emoji: "📥", label: "Preparo tutto…" },
  chunked: { step: 2, emoji: "✂️", label: "Divido il documento in sezioni…" },
  embedded: { step: 3, emoji: "🧠", label: "Imparo il contenuto…" },
  validated: { step: 4, emoji: "🔍", label: "Controllo le contraddizioni…" },
  synthesized: { step: 5, emoji: "✍️", label: "Scrivo le pagine wiki…" },
  edged: { step: 6, emoji: "🔗", label: "Collego le pagine tra loro…" },
  done: { step: 6, emoji: "✅", label: "Fatto!" },
};

const DEFAULT_STEP = { step: 1, emoji: "🤖", label: "Ci sto lavorando…" };

/** Resolve a (possibly null/unknown) backend stage to a display step. */
export function synthesisStep(stage: string | null | undefined): {
  step: number;
  emoji: string;
  label: string;
} {
  return (stage && STAGE_STEPS[stage]) || DEFAULT_STEP;
}

export function SynthesisProgress({
  stage,
  variant = "full",
}: {
  stage: string | null | undefined;
  variant?: "full" | "compact";
}): JSX.Element {
  const { step, emoji, label } = synthesisStep(stage);
  const pct = Math.round((step / TOTAL_STEPS) * 100);

  if (variant === "compact") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 132 }}>
        <span
          style={{
            fontSize: 11.5,
            color: "var(--c-ink-2)",
            display: "flex",
            gap: 5,
            alignItems: "center",
          }}
        >
          <span aria-hidden>{emoji}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {label}
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: "var(--c-line)",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "block",
                height: "100%",
                width: `${pct}%`,
                background: "var(--c-info)",
                transition: "width 600ms cubic-bezier(.2,.7,.2,1)",
              }}
            />
          </span>
          <span className="t-mono" style={{ fontSize: 10, color: "var(--c-ink-3)" }}>
            {step}/{TOTAL_STEPS}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        maxWidth: 380,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "var(--c-ink)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          className="spinner"
          style={{
            width: 14,
            height: 14,
            borderTopColor: "var(--c-ai)",
            borderColor: "var(--c-ai-soft)",
          }}
        />
        🤖 Ci sto lavorando…
      </div>
      <div style={{ fontSize: 14, color: "var(--c-ink-2)" }}>
        <span aria-hidden style={{ marginRight: 6 }}>
          {emoji}
        </span>
        {label}
      </div>
      <div style={{ width: 300, maxWidth: "100%" }}>
        <div
          style={{
            height: 8,
            borderRadius: 5,
            background: "var(--c-bg-elev)",
            border: "1px solid var(--c-line)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "linear-gradient(90deg, var(--c-info), var(--c-ai))",
              transition: "width 700ms cubic-bezier(.2,.7,.2,1)",
            }}
          />
        </div>
        <div className="t-mono" style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 6 }}>
          fase {step} / {TOTAL_STEPS}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>di solito ~30 secondi</div>
    </div>
  );
}
